import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyInvoicePreview } from '../../services/billingService';
import { requestDurationOverride } from '../../services/classService';
import { formatDate, formatTime } from '../../utils/helpers';
import { useToast } from '../../context/ToastContext';
import styles from './MentorInvoice.module.css';

const MentorInvoice = () => {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [durationTarget, setDurationTarget] = useState(null);
  const [durationValues, setDurationValues] = useState({ manual_duration_minutes: '', reason: '' });
  const [isSubmittingDuration, setIsSubmittingDuration] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      const data = await getMyInvoicePreview();
      setBilling(data);
    } catch (error) {
      console.error('Error fetching invoice preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error, fallback = 'Something went wrong') => {
    return error?.response?.data?.detail || error?.message || fallback;
  };

  const handleRequestDuration = (lineItem) => {
    setDurationTarget(lineItem);
    setDurationValues({ manual_duration_minutes: '', reason: '' });
    setShowDurationModal(true);
  };

  const confirmDurationOverride = async () => {
    if (!durationValues.manual_duration_minutes || !durationValues.reason.trim()) {
      toast.warning('Please enter the duration and a reason');
      return;
    }
    setIsSubmittingDuration(true);
    try {
      await requestDurationOverride(durationTarget.class_session_id, {
        manual_duration_minutes: parseInt(durationValues.manual_duration_minutes),
        reason: durationValues.reason,
      });
      toast.success('Duration review requested — a super admin will review it');
      setShowDurationModal(false);
      setDurationTarget(null);
      fetchBilling();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error requesting duration review'));
    } finally {
      setIsSubmittingDuration(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  if (!billing) {
    return (
      <div className={styles.invoicePage}>
        <Card><p className={styles.empty}>Unable to load invoice data</p></Card>
      </div>
    );
  }

  return (
    <div className={styles.invoicePage}>
      <h1>My Invoice</h1>

      <Card className={styles.summaryCard}>
        <div className={styles.summaryGrid}>
          <div>
            <span className={styles.summaryLabel}>Mentor</span>
            <strong>{billing.mentor_name}</strong>
          </div>
          <div>
            <span className={styles.summaryLabel}>Billing Cycle</span>
            <strong>{formatDate(billing.cycle_start)} – {formatDate(billing.cycle_end)}</strong>
          </div>
          <div>
            <span className={styles.summaryLabel}>Hourly Rate</span>
            <strong>{billing.hourly_rate != null ? `₹${billing.hourly_rate}` : 'Not set'}</strong>
          </div>
          <div>
            <span className={styles.summaryLabel}>Total Billable Hours</span>
            <strong>{billing.total_billable_hours}</strong>
          </div>
          <div>
            <span className={styles.summaryLabel}>Total Payout</span>
            <strong>₹{billing.total_payout}</strong>
          </div>
          {billing.needs_verification_count > 0 && (
            <div>
              <span className={styles.summaryLabel}>Needs Verification</span>
              <strong className={styles.flagged}>{billing.needs_verification_count} class{billing.needs_verification_count > 1 ? 'es' : ''}</strong>
            </div>
          )}
        </div>
      </Card>

      <Card>
        {billing.line_items.length === 0 ? (
          <p className={styles.empty}>No classes in the current billing cycle yet</p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.invoiceTable}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Student</th>
                  <th>Hours</th>
                  <th>Hourly Rate</th>
                  <th>Billed Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {billing.line_items.map(li => (
                  <tr key={li.class_session_id} className={li.needs_verification ? styles.flaggedRow : ''}>
                    <td>{formatDate(li.class_date)}</td>
                    <td>{formatTime(li.class_time)}</td>
                    <td>{li.student_name || '—'}</td>
                    <td>{li.billable_hours}</td>
                    <td>₹{billing.hourly_rate ?? '—'}</td>
                    <td>₹{li.payout}</td>
                    <td>
                      {li.needs_verification && (
                        <div className={styles.flaggedCell}>
                          <span className={styles.badge}>Needs Verification</span>
                          {li.duration_override_status === 'none' || li.duration_override_status === 'rejected' ? (
                            <Button size="small" variant="outline" onClick={() => handleRequestDuration(li)}>
                              Request Duration Review
                            </Button>
                          ) : li.duration_override_status === 'pending' ? (
                            <span className={styles.badge} style={{ background: '#1976D2' }}>Review Pending</span>
                          ) : null}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3"><strong>Total</strong></td>
                  <td><strong>{billing.total_billable_hours}</strong></td>
                  <td></td>
                  <td><strong>₹{billing.total_payout}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={showDurationModal} onClose={() => !isSubmittingDuration && setShowDurationModal(false)} title="Request Duration Review">
        <p style={{ marginBottom: '16px' }}>
          No attendance-tracked duration was found for this class on <strong>{durationTarget ? formatDate(durationTarget.class_date) : ''}</strong>.
          Enter the actual minutes conducted and a reason (e.g. "Sembly bot failed to join") for a super admin to review.
        </p>
        <div className={styles.formGroup}>
          <label>Duration Conducted (minutes) *</label>
          <input
            type="number"
            min="1"
            value={durationValues.manual_duration_minutes}
            onChange={(e) => setDurationValues(v => ({ ...v, manual_duration_minutes: e.target.value }))}
          />
        </div>
        <div className={styles.formGroup}>
          <label>Reason *</label>
          <textarea
            rows="3"
            value={durationValues.reason}
            onChange={(e) => setDurationValues(v => ({ ...v, reason: e.target.value }))}
            placeholder="Sembly bot failed to join..."
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
          <Button onClick={confirmDurationOverride} disabled={isSubmittingDuration}>
            {isSubmittingDuration ? 'Submitting…' : 'Submit for Review'}
          </Button>
          <Button variant="outline" onClick={() => setShowDurationModal(false)} disabled={isSubmittingDuration}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default MentorInvoice;
